/**
 * Which build of the web app this browser is actually running.
 *
 * **Baked in at build time, deliberately, rather than read from the API.** The
 * failure this exists to catch is a browser holding an old bundle: the service
 * worker updates on a prompt, an installed web app can sit on yesterday's
 * assets, and CloudFront caches. Asking `/api/health` would report the SERVER's
 * version, which is the one thing that is never in doubt — and it would say
 * "up to date" on exactly the screen that is not.
 *
 * The commit is the same identifier everything else uses: `/api/health`
 * reports it, `scripts/smoke.mjs --expect-version` asserts it, and the release
 * manifest records it. So "I'm on 61765b0" is a sentence somebody can act on.
 * The date in front of it is what a person reads as a version; the commit is
 * what makes it actionable.
 *
 * `JAKS_VERSION` is set by `pnpm deploy:prod` and by `scripts/deploy-prod.sh`.
 * A laptop build has none, and says `dev`.
 */
declare const __JAKS_VERSION__: string;
declare const __JAKS_BUILT_AT__: string;

const commit = typeof __JAKS_VERSION__ === "string" ? __JAKS_VERSION__ : "dev";
const builtAt = typeof __JAKS_BUILT_AT__ === "string" ? __JAKS_BUILT_AT__ : "";

/**
 * `2026.09.06` — dots rather than dashes so it does not read as a date range.
 *
 * **UTC, not local.** Two reasons, and the second is the one that decided it.
 * A version label has to be the SAME STRING for everybody, or two people
 * comparing screens disagree about what they are running. And the release it
 * names is already UTC — `20260906T045248Z-61765b0` — so this way the label's
 * date and the release id's date are the same date. Local time would have read
 * `2026.09.05` in Wichita for that build, which is true and useless.
 *
 * The tooltip shows local time, because "when" is a question about the reader.
 */
function buildDate(iso: string): string {
  const at = new Date(iso);
  if (!iso || Number.isNaN(at.getTime())) return "";
  return `${at.getUTCFullYear()}.${String(at.getUTCMonth() + 1).padStart(2, "0")}.${String(at.getUTCDate()).padStart(2, "0")}`;
}

/**
 * The label and its tooltip, as pure functions of the two build-time values.
 *
 * Pure so they can be tested: the constants below read globals that only exist
 * after Vite has substituted them, and a laptop's build says `dev` — which is
 * exactly the string that FITS, so the one shape a test needs to cover is the
 * one a laptop never renders.
 */
export function versionLabel(commitSha: string, builtAtIso: string): string {
  const short = commitSha === "dev" || !commitSha ? "dev" : commitSha.slice(0, 7);
  return [buildDate(builtAtIso), short].filter(Boolean).join(" · ");
}

export function versionTitle(commitSha: string, builtAtIso: string): string {
  const at = new Date(builtAtIso);
  return [
    commitSha === "dev" || !commitSha ? "JAKS platform, development build" : `JAKS platform ${commitSha}`,
    builtAtIso && !Number.isNaN(at.getTime()) ? `built ${at.toLocaleString()}` : "",
  ].filter(Boolean).join(", ");
}

/** Seven characters, the same short form git, the manifest and the logs use. */
export const VERSION_COMMIT = commit === "dev" ? "dev" : commit.slice(0, 7);
/** What the label says: `2026.09.06 · 61765b0`. */
export const VERSION_LABEL = versionLabel(commit, builtAt);
/** The whole truth, for a tooltip: nobody reads a full commit at 10px. */
export const VERSION_TITLE = versionTitle(commit, builtAt);
